import { Container } from '@cleartrip/ct-design-components'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { getSrpHeroTheme, type SrpHeroPattern } from '@/constants/flights/premiumEconomy'
import { SRP_REVAMP_SHELL_CSS } from '@/constants/Srp'

import CabinHeroVideo from './CabinHeroVideo'

// Left-to-right fade shared by the hero image and the video that fades in over it.
const HERO_MEDIA_MASK = 'linear-gradient(90deg, transparent 0%, #000 26%)'

/** Quote CDN urls — Rukmini `f_auto,q_auto` commas break unquoted CSS `url()`. */
const cssUrl = (src: string) => `url("${src}")`

// Style for the decorative pattern layer. Prefer `kind: 'image'` (background-image)
// for CDN assets — CSS mask-image silently fails cross-origin (same as flights-pwa).
const buildPatternStyle = (pattern: SrpHeroPattern): React.CSSProperties => {
    const { kind, src, color, size, repeat, position, opacity, fade, blur } = pattern
    const filter = blur ? { filter: `blur(${blur}px)` } : {}
    const imageUrl = cssUrl(src)

    if (kind === 'mask') {
        const maskImage = fade ? `${imageUrl}, ${fade}` : imageUrl
        const maskSize = fade ? `${size}, 100% 100%` : size
        const maskRepeat = fade ? `${repeat}, no-repeat` : repeat
        const maskPosition = fade ? `${position}, left top` : position
        return {
            background: color,
            opacity,
            ...filter,
            WebkitMaskImage: maskImage,
            maskImage,
            WebkitMaskSize: maskSize,
            maskSize,
            WebkitMaskRepeat: maskRepeat,
            maskRepeat,
            WebkitMaskPosition: maskPosition,
            maskPosition,
            ...(fade ? { WebkitMaskComposite: 'source-in', maskComposite: 'intersect' } : {}),
        } as React.CSSProperties
    }

    return {
        backgroundImage: imageUrl,
        backgroundRepeat: repeat,
        backgroundSize: size,
        backgroundPosition: position,
        opacity,
        ...filter,
        // Soft edge only — gradient mask needs no CORS; never mask with the CDN asset.
        ...(fade ? { WebkitMaskImage: fade, maskImage: fade } : {}),
    } as React.CSSProperties
}

type PESrpHeroProps = {
    fareType?: string
    cabinClass?: string
    isSrpRevamp?: boolean
    children: React.ReactNode
}

const HERO_CONTENT_MAX_WIDTH = 1264

/**
 * Cabin-themed SRP hero, resolved via getSrpHeroTheme.
 * Clipping lives only on the decorative background layer so the search-bar
 * dropdowns aren't cut off; the search wrapper takes z-30 to clear the results,
 * while the title stays at z-1 (above the hero art, below the sticky filters).
 */
const PESrpHero: React.FC<PESrpHeroProps> = ({ fareType = '', cabinClass, isSrpRevamp = false, children }) => {
    const theme = getSrpHeroTheme(cabinClass)

    // Once the cabin video can play, fade out the poster image so it stops
    // bleeding through the video's transparent left edge (shared left-fade mask).
    const [isVideoReady, setIsVideoReady] = useState(false)

    // Reset when the cabin (and thus the video src) changes, so a newly-selected
    // tab's poster isn't hidden before its own video is ready.
    useEffect(() => {
        setIsVideoReady(false)
    }, [theme?.heroVideo])

    // Revamp drops the legacy FF scale/negative margins for the shared 1280px shell,
    // since its search widget is left-aligned and would clip under the scale.
    const contentStyle: React.CSSProperties =
        fareType === 'FF' && !isSrpRevamp
            ? { scale: '0.90', margin: '-8px -96px 0px -96px' }
            : {}
    const contentClassName = isSrpRevamp ? 'p-relative z-30 px-7' : 'p-relative z-30'
    const contentCss = isSrpRevamp ? SRP_REVAMP_SHELL_CSS : undefined
    const titleClassName = isSrpRevamp ? 'p-relative z-1 mt-5 px-7' : 'p-relative z-1 mt-5'
    const titleCss = isSrpRevamp
        ? SRP_REVAMP_SHELL_CSS
        : { maxWidth: `${HERO_CONTENT_MAX_WIDTH}px`, margin: '0 auto', paddingLeft: '48px' }

    if (!theme) return <>{children}</>

    return (
        <Container
            className="p-relative pb-6"
            // Figma 4094:53359 — search selector sits 24px below the top of the
            // themed hero (PE / Business / First). Default SRP keeps pt-2 (8px).
            css={{ paddingTop: '24px' }}
        >
            {/* decorative background layer (clips the hero image bleed) */}
            <Container
                aria-hidden
                className="p-absolute t-0 l-0 r-0 b-0 o-hidden disable-pointer-events"
                css={{
                    background: theme.background,
                    borderBottomLeftRadius: theme.borderRadius,
                    borderBottomRightRadius: theme.borderRadius,
                }}
            >
                {theme.overlay && (
                    <Container
                        className="p-absolute t-0 l-0 r-0 b-0"
                        css={{ background: theme.overlay }}
                    />
                )}
                {theme.pattern && (
                    <Container
                        className="p-absolute t-0 l-0 r-0 b-0"
                        css={buildPatternStyle(theme.pattern)}
                    />
                )}
                {/* Hero image paints instantly and stays as the fallback while the
                    video loads (and if it fails to play). */}
                <Image
                    priority
                    alt=""
                    draggable={false}
                    height={theme.heroImageHeight}
                    src={theme.heroImage}
                    style={{
                        position: 'absolute',
                        top: `${theme.heroImageTop}px`,
                        right: 0,
                        width: `${theme.heroImageWidth}px`,
                        height: 'auto',
                        pointerEvents: 'none',
                        maskImage: HERO_MEDIA_MASK,
                        WebkitMaskImage: HERO_MEDIA_MASK,
                        opacity: isVideoReady ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                    }}
                    width={theme.heroImageWidth}
                />
                {/* Muted looping cabin video, lazily mounted after first paint; fades
                    in over the poster image above once it can play. */}
                {theme.heroVideo && (
                    <CabinHeroVideo
                        height={theme.heroImageHeight}
                        maskImage={HERO_MEDIA_MASK}
                        src={theme.heroVideo}
                        top={theme.heroImageTop}
                        width={theme.heroImageWidth}
                        onReady={() => setIsVideoReady(true)}
                    />
                )}
            </Container>

            {/* search bar above the results so its dropdowns aren't clipped */}
            <Container className={contentClassName} css={contentCss} style={contentStyle}>
                {children}
            </Container>

            {/* Title wordmark SVG: z-1 keeps it above the hero art, below sticky filters.
                CDN SVGs (not live text) so designed glyphs don't drift across devices. */}
            <Container className={titleClassName} css={titleCss}>
                <Image
                    priority
                    alt={theme.title}
                    draggable={false}
                    height={theme.titleImageHeight}
                    src={theme.titleImage}
                    style={{
                        display: 'block',
                        width: theme.titleImageWidth,
                        height: theme.titleImageHeight,
                        pointerEvents: 'none',
                    }}
                    width={theme.titleImageWidth}
                />
            </Container>
        </Container>
    )
}

export default PESrpHero
